export const VERSION = 'sku-commerce-bootstrap-v1';
export const MAX_SKUS = 10000;
const STORAGE_KEY = 'yw-sku-bootstrap-pending-v1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const SHA = /^[0-9a-f]{64}$/;
const diagnostics = ['hidden', 'certificate', 'comingSoon', 'editorialUnknown', 'disabled', 'unresolved', 'rebound', 'postT0'];
const physical = ['positive', 'zero', 'stale', 'unknown', 'missingLink'];
const policies = ['normalEnabled', 'explicitHolds', 'preorderEnabled', 'missingSelected', 'missingHeld'];
const identityFields = ['childProductId', 'sizeId', 'productId', 'colorId', 'articul', 'colorCode', 'sizeName', 'offerId', 'isSecret'];
const common = ['ok', 'schemaVersion', 'runId'];
const bindingFields = ['runId', 'previewRevision', 'previewSha256'];
const previewFields = [...common, 'previewRevision', 'previewSha256', 'expiresAt', 't0Count', 'allowlistCount',
  'preservedPolicyCount', 'skippedReasonCounts', 'samples'];
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const integer = (value, min = 0, max = MAX_SKUS) => Number.isSafeInteger(value) && value >= min && value <= max;
const id = value => integer(value, 1, Number.MAX_SAFE_INTEGER);
const iso = value => typeof value === 'string' && Number.isFinite(Date.parse(value)) && new Date(Date.parse(value)).toISOString() === value;
const text = (value, max) => typeof value === 'string' && value.length > 0 && value.length <= max && !!value.trim();
const exact = (value, fields) => record(value) && Object.keys(value).length === fields.length && fields.every(field => Object.prototype.hasOwnProperty.call(value, field));
const dense = (value, max) => Array.isArray(value) && value.length <= max && Array.from({length: value.length}, (_, index) => Object.prototype.hasOwnProperty.call(value, index)).every(Boolean);
export function bootstrapError(kind = 'contract', status = null) {
  const error = new Error('Не удалось проверить данные bootstrap.');
  error.kind = kind; error.status = status; return error;
}
const requireValue = valid => {if (!valid) throw bootstrapError();};
const copy = (value, fields) => Object.fromEntries(fields.map(field => [field, value[field]]));
const binding = value => record(value) && typeof value.runId === 'string' && UUID.test(value.runId) &&
  integer(value.previewRevision, 1, 2147483647) && typeof value.previewSha256 === 'string' && SHA.test(value.previewSha256);
const success = value => record(value) && value.ok === true && value.schemaVersion === VERSION && typeof value.runId === 'string' && UUID.test(value.runId);
const counts = (value, keys, max = MAX_SKUS) => exact(value, keys) && keys.every(key => integer(value[key], 0, max));
const bound = (value, expected) => bindingFields.every(field => value[field] === expected[field]);
const pair = value => exact(value, ['childProductId', 'sizeId']) && id(value.childProductId) && id(value.sizeId);
const sum = value => Object.values(value).reduce((total, number) => total + number, 0);
export function skuKey(row) {return `${row.childProductId}:${row.sizeId}`;}

function readPairs(rows, max = MAX_SKUS) {
  requireValue(dense(rows, max)); const seen = new Set();
  return rows.map(row => {requireValue(pair(row) && !seen.has(skuKey(row))); seen.add(skuKey(row));
    return {childProductId: row.childProductId, sizeId: row.sizeId};});
}
export function previewBody(value = {}) {
  requireValue(record(value) && Object.keys(value).every(key => ['runId', 'approvedSkuKeys'].includes(key)));
  const result = {};
  if (Object.prototype.hasOwnProperty.call(value, 'runId')) {
    requireValue(typeof value.runId === 'string' && UUID.test(value.runId)); result.runId = value.runId;
  }
  if (Object.prototype.hasOwnProperty.call(value, 'approvedSkuKeys')) result.approvedSkuKeys = readPairs(value.approvedSkuKeys);
  return result;
}
export function approvalBody(value) {
  requireValue(exact(value, [...bindingFields, 'confirm']) && binding(value) && value.confirm === true);
  return {...copy(value, bindingFields), confirm: true};
}
export function approvalFromPreview(value) {const checked = readPreview(value); return {...copy(checked, bindingFields), confirm: true};}
export function readPreview(value) {
  requireValue(exact(value, previewFields) && success(value) && binding(value) && iso(value.expiresAt) &&
    integer(value.t0Count) && integer(value.allowlistCount) && integer(value.preservedPolicyCount) &&
    value.allowlistCount + value.preservedPolicyCount <= value.t0Count && counts(value.skippedReasonCounts, diagnostics, 500000));
  const samples = readPairs(value.samples, 20);
  requireValue(samples.length === Math.min(20, value.allowlistCount));
  return {...copy(value, previewFields), skippedReasonCounts: {...value.skippedReasonCounts}, samples};
}
function readIdentity(value) {
  requireValue(exact(value, identityFields) && ['childProductId', 'sizeId', 'productId', 'colorId'].every(field => id(value[field])) &&
    text(value.articul, 255) && text(value.colorCode, 128) && text(value.sizeName, 128) && text(value.offerId, 255) && typeof value.isSecret === 'boolean');
  return copy(value, identityFields);
}
export function readPage(value, preview, offset = 0, limit = 100) {
  const p = readPreview(preview);
  requireValue(exact(value, [...common, 'previewRevision', 'previewSha256', 'expiresAt', 'offset', 'limit', 'count', 'nextOffset', 'rows']) &&
    success(value) && binding(value) && bound(value, p) && value.expiresAt === p.expiresAt && value.count === p.allowlistCount &&
    integer(value.offset) && value.offset === offset && integer(value.limit, 1, 100) && value.limit === limit && dense(value.rows, limit));
  requireValue(value.rows.length === Math.min(limit, Math.max(0, value.count - offset)) &&
    value.nextOffset === (offset + value.rows.length < value.count ? offset + value.rows.length : null));
  return {...value, rows: value.rows.map(readIdentity)};
}
export async function loadAllowlist(fetchPage, preview, isCurrent = () => true) {
  const p = readPreview(preview); const rows = []; const seen = new Set(); let offset = 0;
  do {
    if (!isCurrent()) throw bootstrapError('cancelled');
    if (Date.now() >= Date.parse(p.expiresAt)) throw bootstrapError('expired', 409);
    const raw = await fetchPage({...copy(p, bindingFields), offset, limit: 100});
    if (!isCurrent()) throw bootstrapError('cancelled');
    if (Date.now() >= Date.parse(p.expiresAt)) throw bootstrapError('expired', 409);
    const page = readPage(raw, p, offset);
    for (const row of page.rows) {
      const previous = rows[rows.length - 1];
      requireValue(!seen.has(skuKey(row)) && (!previous || previous.childProductId < row.childProductId ||
        previous.childProductId === row.childProductId && previous.sizeId < row.sizeId));
      seen.add(skuKey(row)); rows.push(row);
    }
    offset = page.nextOffset;
  } while (offset !== null);
  requireValue(rows.length === p.allowlistCount && p.samples.every((sample, index) => skuKey(sample) === skuKey(rows[index]))); return rows;
}
export function readApplied(value, expected) {
  const pending = readPending(expected);
  requireValue(exact(value, [...common, 'previewRevision', 'previewSha256', 'appliedAt', 'inserted', 'preserved', 'alreadyApplied']) &&
    success(value) && binding(value) && bound(value, pending.approval) && iso(value.appliedAt) &&
    value.inserted === pending.expectedInserted && value.preserved === pending.expectedPreserved && typeof value.alreadyApplied === 'boolean');
  return {...value};
}
export function readReadiness(value, runId) {
  const fields = [...common, 'status', 'evaluatedAt', 'previewExpired', 't0Count', 'boundCount', 'allowlistCount',
    'policyBootstrapReady', 'ordinaryPurchaseCompatibilityReady', 'readyForGlobalEnforcement', 'policyCounts',
    'stockCounts', 'normalTargetStockCounts', 'identityReboundCount', 'postT0Count', 'blockedReasons'];
  requireValue(exact(value, fields) && success(value) && value.runId === runId && ['preview', 'applied'].includes(value.status) &&
    iso(value.evaluatedAt) && ['previewExpired', 'policyBootstrapReady', 'ordinaryPurchaseCompatibilityReady'].every(field => typeof value[field] === 'boolean') &&
    value.readyForGlobalEnforcement === false && ['t0Count', 'boundCount', 'allowlistCount', 'identityReboundCount', 'postT0Count'].every(field => integer(value[field])) &&
    value.boundCount + value.identityReboundCount === value.t0Count && value.allowlistCount <= value.t0Count &&
    counts(value.policyCounts, policies) && counts(value.stockCounts, physical) && counts(value.normalTargetStockCounts, physical) &&
    sum(value.stockCounts) === value.boundCount && sum(value.normalTargetStockCounts) <= value.boundCount &&
    value.policyCounts.normalEnabled + value.policyCounts.explicitHolds + value.policyCounts.missingSelected + value.policyCounts.missingHeld === value.boundCount &&
    dense(value.blockedReasons, 20) && value.blockedReasons.every(reason => text(reason, 128) && /^[a-z][a-z0-9_]*$/.test(reason)) &&
    value.blockedReasons.includes('whole_goal_release_gates_not_proven'));
  return {...value, policyCounts: {...value.policyCounts}, stockCounts: {...value.stockCounts},
    normalTargetStockCounts: {...value.normalTargetStockCounts}, blockedReasons: [...value.blockedReasons]};
}
export function readPending(value) {
  requireValue(exact(value, ['actorId', 'approval', 'expectedInserted', 'expectedPreserved', 't0Count']) && id(value.actorId) &&
    integer(value.expectedInserted) && integer(value.expectedPreserved) && integer(value.t0Count) &&
    value.expectedInserted + value.expectedPreserved <= value.t0Count);
  return {...value, approval: approvalBody(value.approval)};
}
export function savePending(value) {
  const pending = readPending(value);
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing !== null && JSON.stringify(readPending(JSON.parse(existing))) !== JSON.stringify(pending)) return false;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending)); return true;
  } catch {return false;}
}
export function loadPending(actorId) {
  try {const raw = sessionStorage.getItem(STORAGE_KEY); if (!raw) return null;
    const pending = readPending(JSON.parse(raw)); return pending.actorId === actorId ? pending : null;
  } catch {return null;}
}
export function clearPending(actorId) {
  if (!loadPending(actorId)) return;
  try {sessionStorage.removeItem(STORAGE_KEY);} catch {/* Recovery may remain; exact server retry is idempotent. */}
}
export function recoveryBody(value = {}) {
  requireValue(record(value) && Object.keys(value).every(key => [...bindingFields, 'offset', 'limit'].includes(key)));
  const supplied = bindingFields.filter(key => Object.prototype.hasOwnProperty.call(value, key));
  const {offset = 0, limit = 100} = value;
  requireValue((supplied.length === 0 || supplied.length === 3 && binding(value)) && integer(offset) &&
    integer(limit, 1, 100) && (supplied.length === 3 || offset === 0));
  return {...(supplied.length ? copy(value, bindingFields) : {}), offset, limit};
}
const recoveryRunFields = [...bindingFields, 'previewAt', 'expiresAt', 'previewExpired', 't0Count', 'allowlistCount'];
export function readRecovery(value, expectedCursor = {}) {
  const cursor = recoveryBody(expectedCursor);
  requireValue(exact(value, ['ok', 'schemaVersion', 'recoveryState', 'historicalOnly', 'approvalUsable', 'evaluatedAt', 'run', 'appliedReceipt', 'page']) &&
    value.ok === true && value.schemaVersion === VERSION && value.historicalOnly === true && value.approvalUsable === false &&
    iso(value.evaluatedAt) && ['none', 'preview', 'applied'].includes(value.recoveryState));
  if (value.recoveryState === 'none') {
    requireValue(!cursor.runId && value.run === null && value.appliedReceipt === null && value.page === null); return {...value};
  }
  const run = value.run;
  requireValue(exact(run, recoveryRunFields) && binding(run) && iso(run.previewAt) && iso(run.expiresAt) &&
    Date.parse(run.previewAt) < Date.parse(run.expiresAt) && typeof run.previewExpired === 'boolean' &&
    run.previewExpired === (Date.parse(value.evaluatedAt) >= Date.parse(run.expiresAt)) && integer(run.t0Count) &&
    integer(run.allowlistCount) && run.allowlistCount <= run.t0Count && (!cursor.runId || bound(run, cursor)));
  const page = value.page;
  requireValue(exact(page, ['offset', 'limit', 'count', 'nextOffset', 'rows']) && page.offset === cursor.offset && page.limit === cursor.limit &&
    page.count === run.allowlistCount && page.offset <= page.count && dense(page.rows, page.limit) &&
    page.rows.length === Math.min(page.limit, page.count - page.offset) &&
    page.nextOffset === (page.offset + page.rows.length < page.count ? page.offset + page.rows.length : null));
  const rows = page.rows.map(readIdentity);
  rows.forEach((row, index) => requireValue(index === 0 || rows[index - 1].childProductId < row.childProductId ||
    rows[index - 1].childProductId === row.childProductId && rows[index - 1].sizeId < row.sizeId));
  let receipt = null;
  if (value.recoveryState === 'applied') {
    receipt = value.appliedReceipt;
    requireValue(exact(receipt, [...common, 'previewRevision', 'previewSha256', 'appliedAt', 'inserted', 'preserved']) &&
      success(receipt) && binding(receipt) && bound(receipt, run) && iso(receipt.appliedAt) && Date.parse(receipt.appliedAt) >= Date.parse(run.previewAt) && receipt.inserted === run.allowlistCount &&
      integer(receipt.preserved) && receipt.inserted + receipt.preserved <= run.t0Count);
    receipt = {...receipt};
  } else requireValue(value.appliedReceipt === null);
  return {...value, run: {...run}, appliedReceipt: receipt, page: {...page, rows}};
}
export async function loadRecovery(fetchPage, isCurrent = () => true) {
  if (!isCurrent()) throw bootstrapError('cancelled');
  const firstRaw = await fetchPage({});
  if (!isCurrent()) throw bootstrapError('cancelled');
  const first = readRecovery(firstRaw); const rows = [];
  if (first.recoveryState === 'none') return {recovery: first, rows};
  let page = first;
  do {
    for (const row of page.page.rows) {
      const previous = rows[rows.length - 1];
      requireValue(!previous || previous.childProductId < row.childProductId || previous.childProductId === row.childProductId && previous.sizeId < row.sizeId);
      rows.push(row);
    }
    if (page.page.nextOffset === null) break;
    if (!isCurrent()) throw bootstrapError('cancelled');
    const cursor = {...copy(first.run, bindingFields), offset: page.page.nextOffset, limit: 100};
    const raw = await fetchPage(cursor);
    if (!isCurrent()) throw bootstrapError('cancelled');
    page = readRecovery(raw, cursor);
    requireValue(page.recoveryState === first.recoveryState && recoveryRunFields.filter(key => key !== 'previewExpired').every(key => page.run[key] === first.run[key]) &&
      JSON.stringify(page.appliedReceipt) === JSON.stringify(first.appliedReceipt));
  } while (true);
  requireValue(rows.length === first.run.allowlistCount); return {recovery: first, rows};
}
export function inspectPending(actorId) {
  let raw;
  try {raw = sessionStorage.getItem(STORAGE_KEY);} catch {return {state: 'unavailable'};}
  if (raw === null) return {state: 'none'};
  try {
    const pending = readPending(JSON.parse(raw));
    return pending.actorId === actorId ? {state: 'owned-valid', pending} : {state: 'foreign'};
  } catch {return {state: 'corrupt'};}
}
